import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { RouteError, RouteNotFound } from "@/components/route-error";
import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import {
  Bug,
  Check,
  Copy,
  Loader2,
  Mail,
  Plus,
  ShieldCheck,
  Ticket,
  UserCog,
  Users,
  X,
  Activity,
} from "lucide-react";
import { Cpu, Sparkles, Gauge } from "lucide-react";
import { MessageSquare } from "lucide-react";
import { EngineTab } from "@/components/admin/engine-tab";
import { TestersTab } from "@/components/admin/testers-tab";
import { EngineHealthCard } from "@/components/admin/engine-health-card";
import { AccountCoverageCard } from "@/components/admin/account-coverage-card";
import { SyncHistoryPanel } from "@/components/sync-history-panel";
import { EmailTab } from "@/components/admin/email-tab";
import { FeedbackTab } from "@/components/admin/feedback-tab";
import { QaTab } from "@/components/admin/qa-tab";
import { AccountChip } from "@/components/account-chip";
import { useIsAdminStatus } from "@/hooks/use-is-admin";
import { PLANS, type PlanId } from "@/lib/plans";
import {
  createInviteCode,
  getSystemHealth,
  listAccessRequests,
  listAdminUsers,
  listInviteCodes,
  reviewAccessRequest,
  revokeInviteCode,
  setUserAdmin,
  setUserPlan,
  setUserApproved,
} from "@/lib/admin.functions";
import { listUpgradeRequests, resolveUpgradeRequest } from "@/lib/admin.functions";

export const Route = createFileRoute("/_authenticated/admin")({
  errorComponent: RouteError,
  notFoundComponent: RouteNotFound,
  component: AdminPage,
  head: () => ({
    meta: [
      { title: "Admin · Pyng" },
      { name: "description", content: "Access requests, invite codes, members and system health for Pyng." },
      { property: "og:title", content: "Admin · Pyng" },
      { property: "og:description", content: "Access requests, invite codes, members and system health for Pyng." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
});

type Tab = "requests" | "codes" | "users" | "testers" | "upgrades" | "health" | "engine" | "email" | "feedback" | "qa";

const TABS: { id: Tab; label: string; icon: any }[] = [
  { id: "requests", label: "Access requests", icon: ShieldCheck },
  { id: "codes", label: "Invite codes", icon: Ticket },
  { id: "users", label: "Members", icon: Users },
  { id: "testers", label: "Testers", icon: Gauge },
  { id: "upgrades", label: "Upgrades", icon: Sparkles },
  { id: "health", label: "System health", icon: Activity },
  { id: "engine", label: "Engine", icon: Cpu },
  { id: "email", label: "Email", icon: Mail },
  { id: "feedback", label: "Feedback", icon: MessageSquare },
  { id: "qa", label: "QA", icon: Bug },
];

function fmt(iso?: string | null) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

function Card({ children }: { children: React.ReactNode }) {
  return <div className="glass rounded-3xl p-4 sm:p-5">{children}</div>;
}

function Empty({ children }: { children: React.ReactNode }) {
  return <p className="px-1 py-8 text-center text-sm text-muted-foreground">{children}</p>;
}

function AdminPage() {
  const { isAdmin, isLoading: adminLoading } = useIsAdminStatus();
  const navigate = useNavigate();
  const [tab, setTab] = useState<Tab>("requests");

  // Only redirect once the role check has actually answered.
  useEffect(() => {
    if (!adminLoading && !isAdmin) navigate({ to: "/today" });
  }, [adminLoading, isAdmin, navigate]);

  if (!isAdmin) {
    return (
      <div className="grid min-h-[50vh] place-items-center text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-5 pb-6">
      <header className="flex items-start justify-between gap-4 pt-1">
        <h1 className="text-[26px] font-semibold leading-tight tracking-tight sm:text-3xl md:text-4xl">Admin</h1>
        <div className="shrink-0"><AccountChip /></div>
      </header>

      <div className="no-scrollbar -mx-1 flex gap-2 overflow-x-auto px-1">
        {TABS.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            onClick={() => setTab(id)}
            className={
              "inline-flex shrink-0 items-center gap-2 rounded-full px-3.5 py-2 text-[13px] font-medium transition-colors " +
              (tab === id
                ? "bg-foreground/[0.08] text-foreground"
                : "glass-soft text-muted-foreground hover:text-foreground")
            }
          >
            <Icon className="h-3.5 w-3.5" strokeWidth={1.75} />
            {label}
          </button>
        ))}
      </div>

      {tab === "requests" && <RequestsTab />}
      {tab === "codes" && <CodesTab />}
      {tab === "users" && <UsersTab />}
      {tab === "testers" && <TestersTab />}
      {tab === "upgrades" && <UpgradesTab />}
      {tab === "health" && <HealthTab />}
      {tab === "engine" && <EngineTab />}
      {tab === "email" && <EmailTab />}
      {tab === "feedback" && <FeedbackTab />}
      {tab === "qa" && <QaTab />}
    </div>
  );
}

/* ---------------------------------------------------------------- requests */

function UpgradesTab() {
  const qc = useQueryClient();
  const load = useServerFn(listUpgradeRequests);
  const resolve = useServerFn(resolveUpgradeRequest);
  const { data, isLoading } = useQuery({ queryKey: ["admin-upgrades"], queryFn: () => load() });

  const act = useMutation({
    mutationFn: (v: { id: string; status: "done" | "declined" }) => resolve({ data: v }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-upgrades"] });
      toast.success("Updated");
    },
    onError: (e: any) => toast.error(e?.message ?? "Failed"),
  });

  const rows = (data as any)?.requests ?? [];

  return (
    <Card>
      <p className="mb-3 text-[13px] text-muted-foreground">
        In-app “Request Pro” taps. Billing isn't wired up yet — flip the plan on the Members tab, then mark it done.
      </p>
      {isLoading ? (
        <Empty>Loading…</Empty>
      ) : rows.length === 0 ? (
        <Empty>No upgrade requests yet.</Empty>
      ) : (
        <ul className="divide-y divide-foreground/10">
          {rows.map((r: any) => (
            <li key={r.id} className="flex flex-wrap items-center gap-3 py-3 text-sm">
              <div className="min-w-0 flex-1">
                <p className="truncate font-medium">
                  {r.profile?.display_name || r.profile?.full_name || r.profile?.email || r.user_id}
                </p>
                <p className="truncate text-[12px] text-muted-foreground">
                  wants {r.plan} · from {r.source ?? "app"} · {fmt(r.created_at)}
                  {r.profile?.plan ? ` · now on ${r.profile.plan}` : ""}
                </p>
              </div>
              {r.status === "pending" ? (
                <div className="flex shrink-0 gap-2">
                  <button
                    onClick={() => act.mutate({ id: r.id, status: "done" })}
                    className="inline-flex h-8 items-center gap-1 rounded-full glass-soft px-3 text-[13px]"
                  >
                    <Check className="h-3.5 w-3.5" /> Done
                  </button>
                  <button
                    onClick={() => act.mutate({ id: r.id, status: "declined" })}
                    className="inline-flex h-8 items-center gap-1 rounded-full glass-soft px-3 text-[13px] text-muted-foreground"
                  >
                    <X className="h-3.5 w-3.5" /> Dismiss
                  </button>
                </div>
              ) : (
                <span className="shrink-0 text-[12px] text-muted-foreground">{r.status}</span>
              )}
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

function RequestsTab() {
  const qc = useQueryClient();
  const load = useServerFn(listAccessRequests);
  const review = useServerFn(reviewAccessRequest);
  const [filter, setFilter] = useState<"pending" | "approved" | "declined" | "all">("pending");
  const [q, setQ] = useState("");

  const { data, isLoading } = useQuery({ queryKey: ["admin-requests"], queryFn: () => load() });

  const decide = useMutation({
    mutationFn: (vars: { id: string; decision: "approved" | "declined"; plan?: "free" | "pro" | "team" }) =>
      review({ data: { plan: "free", ...vars } as any }),
    onSuccess: (res: any, vars) => {
      qc.invalidateQueries({ queryKey: ["admin-requests"] });
      qc.invalidateQueries({ queryKey: ["admin-codes"] });
      if (vars.decision === "approved") {
        const link = `${window.location.origin}/login?code=${res?.code ?? ""}`;
        navigator.clipboard?.writeText(link);
        toast.success(`Approved${vars.plan === "pro" ? " as Pro" : ""} · invite link copied`);
      } else toast.success("Declined");
    },
    onError: (e: any) => toast.error(e?.message ?? "Could not update that request"),
  });

  const rows = (data?.requests ?? []).filter((r: any) => {
    if (filter !== "all" && (r.status ?? "pending") !== filter) return false;
    if (!q.trim()) return true;
    const hay = `${r.name} ${r.title} ${r.email}`.toLowerCase();
    return hay.includes(q.trim().toLowerCase());
  });

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        {(["pending", "approved", "declined", "all"] as const).map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={
              "rounded-full px-3 py-1.5 text-xs font-medium capitalize transition-colors " +
              (filter === f ? "bg-foreground/[0.08] text-foreground" : "glass-soft text-muted-foreground hover:text-foreground")
            }
          >
            {f}
          </button>
        ))}
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search name or email"
          className="glass-soft ml-auto w-full min-w-0 rounded-full px-3.5 py-1.5 text-xs outline-none placeholder:text-muted-foreground sm:w-56"
        />
      </div>

      {isLoading ? (
        <Card><Empty>Loading requests…</Empty></Card>
      ) : rows.length === 0 ? (
        <Card><Empty>Nothing here.</Empty></Card>
      ) : (
        <div className="space-y-2.5">
          {rows.map((r: any) => (
            <Card key={r.id}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="truncate text-[15px] font-medium">{r.name}</p>
                  <p className="truncate text-[13px] text-muted-foreground">{r.title}</p>
                  <p className="mt-1 truncate text-xs text-muted-foreground">{r.email}</p>
                  <p className="mt-1 text-[11px] text-muted-foreground/80">
                    Applied {fmt(r.created_at)}
                    {r.referrer ? ` · via ${new URL(r.referrer, "https://x.dev").hostname}` : ""}
                  </p>
                  {r.invite_code && (
                    <CopyRow value={r.invite_code} />
                  )}
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  {(r.status ?? "pending") === "pending" ? (
                    <>
                      <button
                        disabled={decide.isPending}
                        onClick={() => decide.mutate({ id: r.id, decision: "approved", plan: "free" })}
                        className="inline-flex h-9 items-center gap-1.5 rounded-full bg-primary px-3.5 text-[13px] font-medium text-primary-foreground disabled:opacity-60"
                      >
                        <Check className="h-3.5 w-3.5" /> Approve
                      </button>
                      <button
                        disabled={decide.isPending}
                        onClick={() => decide.mutate({ id: r.id, decision: "approved", plan: "pro" })}
                        className="inline-flex h-9 items-center gap-1.5 rounded-full border border-[color-mix(in_oklab,var(--accent-neon)_45%,transparent)] px-3.5 text-[13px] font-medium text-foreground disabled:opacity-60"
                        style={{ boxShadow: "0 0 18px -8px var(--accent-neon)" }}
                      >
                        <Check className="h-3.5 w-3.5" /> Approve as Pro
                      </button>
                      <button
                        disabled={decide.isPending}
                        onClick={() => decide.mutate({ id: r.id, decision: "declined" })}
                        className="glass-soft inline-flex h-9 items-center gap-1.5 rounded-full px-3.5 text-[13px] text-muted-foreground hover:text-foreground disabled:opacity-60"
                      >
                        <X className="h-3.5 w-3.5" /> Decline
                      </button>
                    </>
                  ) : (
                    <span
                      className={
                        "rounded-full px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider " +
                        (r.status === "approved"
                          ? "bg-emerald-500/15 text-emerald-500"
                          : "bg-foreground/[0.07] text-muted-foreground")
                      }
                    >
                      {r.status}
                    </span>
                  )}
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

function CopyRow({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      onClick={() => {
        navigator.clipboard?.writeText(value);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      }}
      className="glass-soft mt-2 inline-flex items-center gap-2 rounded-full px-3 py-1.5 font-mono text-[11px] tracking-wider hover:text-foreground"
    >
      {value}
      {copied ? <Check className="h-3 w-3 text-emerald-500" /> : <Copy className="h-3 w-3 opacity-60" />}
    </button>
  );
}

/* ------------------------------------------------------------------- codes */

function CodesTab() {
  const qc = useQueryClient();
  const load = useServerFn(listInviteCodes);
  const create = useServerFn(createInviteCode);
  const revoke = useServerFn(revokeInviteCode);
  const [label, setLabel] = useState("");
  const [maxUses, setMaxUses] = useState(1);
  const [expiresInDays, setExpires] = useState<number | null>(null);
  const [grantPlan, setGrantPlan] = useState<"free" | "pro" | "team">("free");
  const [trialDays, setTrialDays] = useState<number | null>(null);

  const { data, isLoading } = useQuery({ queryKey: ["admin-codes"], queryFn: () => load() });

  const make = useMutation({
    mutationFn: () =>
      create({ data: { label: label || undefined, maxUses, expiresInDays, plan: grantPlan, trialDays } as any }),
    onSuccess: (res: any) => {
      setLabel("");
      qc.invalidateQueries({ queryKey: ["admin-codes"] });
      toast.success(`Code created · ${res?.code ?? ""}`);
    },
    onError: (e: any) => toast.error(e?.message ?? "Could not create a code"),
  });

  const kill = useMutation({
    mutationFn: (id: string) => revoke({ data: { id } }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-codes"] });
      toast.success("Code revoked");
    },
    onError: (e: any) => toast.error(e?.message ?? "Could not revoke that code"),
  });

  return (
    <div className="space-y-3">
      <Card>
        <p className="text-[13px] font-medium">New Friends &amp; Family code</p>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <input
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="Label (who is this for?)"
            className="glass-soft min-w-0 flex-1 rounded-full px-3.5 py-2 text-[13px] outline-none placeholder:text-muted-foreground"
          />
          <label className="glass-soft inline-flex items-center gap-2 rounded-full px-3.5 py-2 text-[13px] text-muted-foreground">
            Uses
            <input
              type="number"
              min={1}
              max={1000}
              value={maxUses}
              onChange={(e) => setMaxUses(Math.max(1, Number(e.target.value) || 1))}
              className="w-14 bg-transparent text-foreground outline-none"
            />
          </label>
          <select
            value={expiresInDays ?? ""}
            onChange={(e) => setExpires(e.target.value ? Number(e.target.value) : null)}
            className="glass-soft rounded-full px-3.5 py-2 text-[13px] text-muted-foreground outline-none"
          >
            <option value="">Never expires</option>
            <option value="7">Expires in 7 days</option>
            <option value="30">Expires in 30 days</option>
            <option value="90">Expires in 90 days</option>
          </select>
          <select
            value={grantPlan}
            onChange={(e) => setGrantPlan(e.target.value as any)}
            className="glass-soft rounded-full px-3.5 py-2 text-[13px] text-muted-foreground outline-none"
          >
            <option value="free">Grants Free</option>
            <option value="pro">Grants Pro</option>
            <option value="team">Grants Team</option>
          </select>
          {grantPlan !== "free" && (
            <select
              value={trialDays ?? ""}
              onChange={(e) => setTrialDays(e.target.value ? Number(e.target.value) : null)}
              className="glass-soft rounded-full px-3.5 py-2 text-[13px] text-muted-foreground outline-none"
            >
              <option value="">Forever</option>
              <option value="30">For 30 days</option>
              <option value="60">For 60 days</option>
              <option value="90">For 90 days</option>
            </select>
          )}
          <button
            disabled={make.isPending}
            onClick={() => make.mutate()}
            className="inline-flex h-9 items-center gap-1.5 rounded-full bg-primary px-3.5 text-[13px] font-medium text-primary-foreground disabled:opacity-60"
          >
            <Plus className="h-3.5 w-3.5" /> Create
          </button>
        </div>
      </Card>

      {isLoading ? (
        <Card><Empty>Loading codes…</Empty></Card>
      ) : (data?.codes ?? []).length === 0 ? (
        <Card><Empty>No invite codes yet.</Empty></Card>
      ) : (
        <div className="space-y-2.5">
          {(data?.codes ?? []).map((c: any) => {
            const dead = !!c.revoked_at || (c.expires_at && new Date(c.expires_at) < new Date()) || c.used_count >= c.max_uses;
            return (
              <Card key={c.id}>
                <div className={"flex flex-wrap items-center justify-between gap-3 " + (dead ? "opacity-60" : "")}>
                  <div className="min-w-0">
                    <CopyRow value={c.code} />
                    <p className="mt-1 truncate text-[13px]">{c.label || "Unlabelled"}</p>
                    <p className="text-[11px] text-muted-foreground">
                      {c.used_count}/{c.max_uses} used · {c.revoked_at ? "revoked" : c.expires_at ? `expires ${fmt(c.expires_at)}` : "no expiry"}
                      {c.grants_plan && c.grants_plan !== "free"
                        ? ` · grants ${String(c.grants_plan).toUpperCase()}${c.trial_days ? ` for ${c.trial_days}d` : ""}`
                        : ""}
                    </p>
                  </div>
                  {!c.revoked_at && (
                    <button
                      disabled={kill.isPending}
                      onClick={() => kill.mutate(c.id)}
                      className="glass-soft inline-flex h-9 items-center gap-1.5 rounded-full px-3.5 text-[13px] text-muted-foreground hover:text-foreground disabled:opacity-60"
                    >
                      Revoke
                    </button>
                  )}
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------- users */

function UsersTab() {
  const qc = useQueryClient();
  const load = useServerFn(listAdminUsers);
  const changePlan = useServerFn(setUserPlan);
  const changeAdmin = useServerFn(setUserAdmin);
  const changeApproved = useServerFn(setUserApproved);
  const [q, setQ] = useState("");

  const { data, isLoading } = useQuery({ queryKey: ["admin-users"], queryFn: () => load() });

  const plan = useMutation({
    mutationFn: (vars: { userId: string; plan: PlanId }) => changePlan({ data: vars }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-users"] });
      toast.success("Plan updated");
    },
    onError: (e: any) => toast.error(e?.message ?? "Could not change that plan"),
  });

  const role = useMutation({
    mutationFn: (vars: { userId: string; makeAdmin: boolean }) => changeAdmin({ data: vars }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-users"] });
      toast.success("Access updated");
    },
    onError: (e: any) => toast.error(e?.message ?? "Could not change access"),
  });

  const approval = useMutation({
    mutationFn: (vars: { userId: string; approved: boolean }) => changeApproved({ data: vars }),
    onSuccess: (_r, vars) => {
      qc.invalidateQueries({ queryKey: ["admin-users"] });
      toast.success(vars.approved ? "Let in" : "Access paused");
    },
    onError: (e: any) => toast.error(e?.message ?? "Could not change approval"),
  });

  const rows = (data?.users ?? []).filter((u: any) =>
    !q.trim() ? true : `${u.name} ${u.email ?? ""}`.toLowerCase().includes(q.trim().toLowerCase()),
  );

  return (
    <div className="space-y-3">
      <input
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Search members"
        className="glass-soft w-full rounded-full px-3.5 py-2 text-[13px] outline-none placeholder:text-muted-foreground sm:w-72"
      />

      {isLoading ? (
        <Card><Empty>Loading members…</Empty></Card>
      ) : rows.length === 0 ? (
        <Card><Empty>No members match that.</Empty></Card>
      ) : (
        <div className="space-y-2.5">
          {rows.map((u: any) => (
            <Card key={u.id}>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="truncate text-[15px] font-medium">
                    {u.name}
                    {u.isAdmin && (
                      <span className="ml-2 rounded-full bg-primary/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-primary">
                        Admin
                      </span>
                    )}
                    {!u.approved && (
                      <span className="ml-2 rounded-full bg-amber-500/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-amber-500">
                        Waitlisted
                      </span>
                    )}
                  </p>
                  <p className="truncate text-[13px] text-muted-foreground">{u.email}</p>
                  <p className="mt-1 text-[11px] text-muted-foreground/80">
                    {u.contactCount} people · {u.dailyTarget}/day · joined {fmt(u.createdAt)}
                    {u.onboarded ? "" : " · not onboarded"}
                    {u.planExpiresAt ? ` · ${u.plan.toUpperCase()} until ${fmt(u.planExpiresAt)}` : ""}
                  </p>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <select
                    value={u.plan}
                    disabled={plan.isPending}
                    onChange={(e) => plan.mutate({ userId: u.id, plan: e.target.value as PlanId })}
                    className="glass-soft rounded-full px-3 py-2 text-[13px] outline-none"
                  >
                    {PLANS.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name}
                      </option>
                    ))}
                  </select>
                  <button
                    disabled={approval.isPending}
                    onClick={() => approval.mutate({ userId: u.id, approved: !u.approved })}
                    className="glass-soft inline-flex h-9 items-center gap-1.5 rounded-full px-3.5 text-[13px] text-muted-foreground hover:text-foreground disabled:opacity-60"
                  >
                    {u.approved ? "Pause access" : "Let in"}
                  </button>
                  <button
                    disabled={role.isPending}
                    onClick={() => role.mutate({ userId: u.id, makeAdmin: !u.isAdmin })}
                    className="glass-soft inline-flex h-9 items-center gap-1.5 rounded-full px-3.5 text-[13px] text-muted-foreground hover:text-foreground disabled:opacity-60"
                  >
                    <UserCog className="h-3.5 w-3.5" />
                    {u.isAdmin ? "Revoke admin" : "Make admin"}
                  </button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ health */

function HealthTab() {
  const load = useServerFn(getSystemHealth);
  const { data, isLoading } = useQuery({ queryKey: ["admin-health"], queryFn: () => load(), refetchInterval: 60_000 });

  if (isLoading) return <Card><Empty>Loading health…</Empty></Card>;

  const t = data?.totals;
  const coverage = (data as any)?.coverage as
    | Record<string, { total: number; covered: number; windowHours: number }>
    | undefined;
  const onDemand = (data as any)?.onDemand;
  const monitor = (data?.jobs ?? []).find((j: any) => j.job === "monitor-people");
  const tierScanned = monitor?.stats?.tierScanned ?? {};
  const vipStarved = Number(monitor?.stats?.vipStarvedByCap ?? 0);
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-4">
        {[
          { label: "Members", value: t?.users ?? 0 },
          { label: "People tracked", value: t?.contacts ?? 0 },
          { label: "Signals (24h)", value: t?.signals24h ?? 0 },
          { label: "Opportunities (24h)", value: t?.opportunities24h ?? 0 },
        ].map((s) => (
          <div key={s.label} className="glass rounded-3xl p-4">
            <p className="text-2xl font-semibold tracking-tight">{s.value}</p>
            <p className="mt-0.5 text-[11px] text-muted-foreground">{s.label}</p>
          </div>
        ))}
      </div>

      <EngineHealthCard />
      <AccountCoverageCard />

      <SyncHistoryPanel scope="admin" />

      <Card>
        <p className="text-[13px] font-medium">Scan coverage</p>
        <p className="mt-0.5 text-[11px] text-muted-foreground">
          People scanned inside their tier's window.
        </p>
        <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-3">
          {(["vip", "active", "standard"] as const).map((tier) => {
            const c = coverage?.[tier];
            const total = c?.total ?? 0;
            const covered = c?.covered ?? 0;
            const pct = total ? Math.round((covered / total) * 100) : 100;
            const label = tier === "vip" ? "VIP · daily" : tier === "active" ? "Active · 3 days" : "Standard · weekly";
            const good = pct >= 80;
            return (
              <div key={tier} className="glass-soft rounded-2xl px-3.5 py-3">
                <div className="flex items-baseline justify-between gap-2">
                  <p className="text-[11px] text-muted-foreground">{label}</p>
                  <span
                    className={
                      "rounded-full px-2 py-0.5 text-[11px] font-semibold " +
                      (good ? "bg-emerald-500/15 text-emerald-500" : "bg-amber-500/15 text-amber-500")
                    }
                  >
                    {pct}%
                  </span>
                </div>
                <p className="mt-1 text-[13px] font-medium">
                  {covered} / {total} people
                </p>
                <p className="text-[11px] text-muted-foreground">
                  Last run scanned {Number(tierScanned?.[tier] ?? 0)}
                </p>
              </div>
            );
          })}
        </div>
        {vipStarved > 0 && (
          <p className="mt-2.5 rounded-2xl bg-amber-500/10 px-3 py-2 text-[11px] font-medium text-amber-600">
            {vipStarved} VIP{vipStarved === 1 ? "" : "s"} were skipped by the per-run cap on the last scan.
          </p>
        )}
      </Card>

      <Card>
        <p className="text-[13px] font-medium">On-demand scans (24h)</p>
        <p className="mt-0.5 text-[11px] text-muted-foreground">
          Adding a person and manual Boost.
        </p>
        <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
          {(onDemand?.summary ?? []).map((s: any) => {
            const rate = s.runs ? Math.round((s.ok / s.runs) * 100) : 100;
            return (
              <div key={s.job} className="glass-soft rounded-2xl px-3.5 py-3">
                <div className="flex items-baseline justify-between gap-2">
                  <p className="text-[11px] text-muted-foreground">
                    {s.job === "onboard-person" ? "Adding people" : "Boost"}
                  </p>
                  <span
                    className={
                      "rounded-full px-2 py-0.5 text-[11px] font-semibold " +
                      (s.failed === 0 ? "bg-emerald-500/15 text-emerald-500" : "bg-red-500/15 text-red-500")
                    }
                  >
                    {rate}% ok
                  </span>
                </div>
                <p className="mt-1 text-[13px] font-medium">
                  {s.runs} run{s.runs === 1 ? "" : "s"} · {s.signals} signals
                </p>
                <p className="text-[11px] text-muted-foreground">Last {fmt(s.lastAt)}</p>
              </div>
            );
          })}
        </div>
        {(onDemand?.failures ?? []).length > 0 && (
          <div className="mt-2.5 space-y-1.5">
            {(onDemand?.failures ?? []).map((f: any) => (
              <p key={f.id} className="rounded-2xl bg-red-500/10 px-3 py-2 text-[11px] text-red-500">
                {f.job === "onboard-person" ? "Add person" : "Boost"} · {fmt(f.startedAt)} ·{" "}
                {String(f.error ?? "failed").slice(0, 120)}
              </p>
            ))}
          </div>
        )}
      </Card>

      <Card>
        <div className="flex items-center justify-between">
          <p className="text-[13px] font-medium">Scan jobs</p>
          {(data?.failures24h ?? 0) > 0 && (
            <span className="rounded-full bg-red-500/15 px-2.5 py-1 text-[11px] font-semibold text-red-500">
              {data?.failures24h} failed in 24h
            </span>
          )}
        </div>
        <div className="mt-3 space-y-2">
          {(data?.jobs ?? []).length === 0 ? (
            <Empty>No scan runs recorded yet.</Empty>
          ) : (
            (data?.jobs ?? []).map((j: any) => (
              <div key={j.job} className="glass-soft flex flex-wrap items-center justify-between gap-2 rounded-2xl px-3.5 py-2.5">
                <div className="min-w-0">
                  <p className="truncate text-[13px] font-medium">{j.job}</p>
                  <p className="text-[11px] text-muted-foreground">
                    Last run {fmt(j.startedAt)}
                    {j.error ? ` · ${String(j.error).slice(0, 90)}` : ""}
                  </p>
                </div>
                <span
                  className={
                    "shrink-0 rounded-full px-2.5 py-1 text-[11px] font-semibold " +
                    (j.ok ? "bg-emerald-500/15 text-emerald-500" : "bg-red-500/15 text-red-500")
                  }
                >
                  {j.ok ? "OK" : "Failed"}
                </span>
              </div>
            ))
          )}
        </div>
      </Card>
    </div>
  );
}